// Localização: frontend/src/components/Prontuario/HistoricoMedicoDocumentos.jsx
// Componente para exibir e gerenciar documentos vinculados ao histórico médico

import React, { useState } from 'react';
import { FileText, Plus, Eye, Download, MoreVertical } from 'lucide-react';
import { visualizarDocumentoPaciente, downloadDocumentoPaciente } from '../../api/api';
import UploadDocumentoModal from './Modals/UploadDocumentoModal';
import DropdownMenu from '../UI/DropdownMenu';
import { useDocumentosPaciente } from '../../hooks/data/useDocumentosPaciente';
import { useNotification } from '../../context/NotificationContext';

const HistoricoMedicoDocumentos = ({ pacienteId, entidadeOrigem, entidadeOrigemId, documentos = [], onDocumentoUploaded }) => {
    const { success: showSuccess, error: showError } = useNotification();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const { uploadDocumento, refetch } = useDocumentosPaciente(pacienteId, {});

    // Converter documentos de string JSON para array se necessário
    const documentosArray = React.useMemo(() => {
        if (!documentos) return [];
        if (Array.isArray(documentos)) return documentos;
        if (typeof documentos === 'string') {
            try {
                return JSON.parse(documentos);
            } catch {
                return [];
            }
        }
        return [];
    }, [documentos]);

    const handleUpload = async (formData) => {
        try {
            // Adicionar informações de vinculação
            formData.append('entidade_origem', entidadeOrigem);
            formData.append('entidade_origem_id', entidadeOrigemId.toString());
            
            // Se não tiver categoria, definir baseado na entidade
            if (!formData.get('categoria')) {
                const categoriaMap = {
                    'historico_medico': 'historico-medico',
                    'alergia': 'historico-medico',
                    'vacina': 'historico-medico',
                    'exame': 'exame',
                    'historico_familiar': 'historico-medico',
                    'notas_clinicas': 'historico-medico'
                };
                const categoria = categoriaMap[entidadeOrigem] || 'historico-medico';
                formData.append('categoria', categoria);
            }

            await uploadDocumento(formData);
            await refetch();
            showSuccess('Documento enviado com sucesso!');
            setIsUploadModalOpen(false);
            // Notificar componente pai para atualizar a lista (ex: notas clínicas)
            if (onDocumentoUploaded) {
                onDocumentoUploaded();
            }
        } catch (error) {
            showError(error?.response?.data?.message || 'Erro ao enviar documento.');
            throw error;
        }
    };

    const handleVisualizar = async (documentoId) => {
        try {
            const url = visualizarDocumentoPaciente(pacienteId, documentoId);
            window.open(url, '_blank');
        } catch (error) {
            showError('Erro ao visualizar documento.');
        }
    };

    const handleDownload = async (documentoId, nomeArquivo) => {
        try {
            const url = downloadDocumentoPaciente(pacienteId, documentoId);
            const link = document.createElement('a');
            link.href = url;
            link.download = nomeArquivo || 'documento.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            showError('Erro ao baixar documento.');
        }
    };

    // Função auxiliar para criar itens do dropdown de ações do documento
    const createDocumentoDropdownItems = (doc) => [
        {
            id: 'visualizar',
            label: 'Visualizar',
            icon: Eye,
            iconColor: 'text-blue-600',
            onClick: () => handleVisualizar(doc.id)
        },
        {
            id: 'download',
            label: 'Baixar',
            icon: Download,
            iconColor: 'text-emerald-600',
            onClick: () => handleDownload(doc.id, doc.nome_arquivo)
        }
    ];

    if (documentosArray.length === 0) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Nenhum documento</span>
                <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                    title="Adicionar documento"
                >
                    <Plus className="w-4 h-4" />
                </button>
                <UploadDocumentoModal
                    isOpen={isUploadModalOpen}
                    onClose={() => setIsUploadModalOpen(false)}
                    onUpload={handleUpload}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            {documentosArray.map((doc) => (
                <div
                    key={doc.id}
                    className="flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-sm"
                >
                    <FileText className="w-3 h-3 text-emerald-600" />
                    <span className="text-emerald-800 truncate max-w-[150px]" title={doc.titulo}>
                        {doc.titulo}
                    </span>
                    <div className="ml-1">
                        <DropdownMenu
                            trigger={
                                <button className="p-0.5 text-emerald-600 hover:bg-emerald-100 rounded transition-colors">
                                    <MoreVertical className="w-3 h-3" />
                                </button>
                            }
                            items={createDocumentoDropdownItems(doc)}
                            position="bottom-right"
                        />
                    </div>
                </div>
            ))}
            <button
                onClick={() => setIsUploadModalOpen(true)}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                title="Adicionar documento"
            >
                <Plus className="w-4 h-4" />
            </button>
            <UploadDocumentoModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleUpload}
            />
        </div>
    );
};

export default HistoricoMedicoDocumentos;

